import { useEffect, useRef, useState } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, captureFeedbackConversationBoundary, listMixedDevelopment, listCanonicalFollowupCandidates } from '@/lib/api';
import { coachingConsumerKey } from '@/lib/queryClient';
import { captureCoachingManualCommand } from '@/lib/coachingManualCommands';
import { isCurrentDraftContext, type DraftContext } from '@/features/coach-review/composerDrafts';
import type { CoachingManualCommand, CoachingManualResult, CoachingManualState, CoachingMixedHistoryResponse,
  CoachingCanonicalCandidatesResponse } from '@/lib/types';
import { isAnchor, isObservation } from './canonicalFollowupForm';

export const accessDenied = (error: unknown) => error instanceof ApiError && [401, 403, 404].includes(error.status);
const abort = () => new DOMException('Coaching context changed', 'AbortError');

/** Own one authority lifetime, including leave-and-return before a render. */
function useLifetime(owner: DraftContext) {
  const alive = useRef(true);
  const boundary = useRef<ReturnType<typeof captureFeedbackConversationBoundary> | null>(null);
  if (!boundary.current) boundary.current = captureFeedbackConversationBoundary({ ...owner, view: 'coach' });
  const current = () => alive.current && !!boundary.current?.current() && isCurrentDraftContext(owner);
  useEffect(() => {
    alive.current = true;
    if (!boundary.current?.current()) boundary.current = captureFeedbackConversationBoundary({ ...owner, view: 'coach' });
    return () => { alive.current = false; boundary.current?.dispose(); };
  }, [owner]);
  return { current, boundary };
}

/** Mounted coordination only; immutable commands and receipts remain shared. */
export function useManualCommand<C extends CoachingManualCommand>(owner: DraftContext) {
  const life = useLifetime(owner);
  const pending = useRef<ReturnType<typeof captureCoachingManualCommand<C>> | null>(null);
  const sending = useRef(false);
  const [, render] = useState(0);
  const [denied, setDenied] = useState(false);
  const state: CoachingManualState<C> | { status: 'idle' } = life.current()
    ? pending.current?.getState() ?? { status: 'idle' } : { status: 'unavailable' };
  useEffect(() => () => pending.current?.dispose(), []);
  const run = async (command?: C): Promise<CoachingManualResult<C> | null> => {
    if (!life.current() || denied || sending.current) return null;
    if (!pending.current && command) pending.current = captureCoachingManualCommand(owner, command);
    const original = pending.current;
    if (!original) return null;
    sending.current = true; render(value => value + 1);
    try {
      const result = await original.send();
      if (!life.current() || pending.current !== original) return null;
      return result;
    } catch (error) {
      if (life.current() && accessDenied(error)) setDenied(true);
      return null;
    } finally {
      sending.current = false;
      if (life.current()) render(value => value + 1);
    }
  };
  const release = () => {
    if (sending.current || !life.current()) return;
    const status = pending.current?.getState().status;
    if (status && status !== 'accepted' && status !== 'rejected') return;
    pending.current?.dispose(); pending.current = null; render(value => value + 1);
  };
  return { state, denied, run, release, current: life.current,
    busy: sending.current, blocked: ['sending', 'unknown', 'retired', 'unavailable'].includes(state.status) };
}

type Kind = 'history' | 'action' | 'focus' | 'support';
type Page = CoachingMixedHistoryResponse | CoachingCanonicalCandidatesResponse;
function useCollection(owner: DraftContext, playerId: string, kind: Kind,
  life: ReturnType<typeof useLifetime>, denied: boolean) {
  const client = useQueryClient();
  const [key] = useState(() => [...coachingConsumerKey(owner, kind === 'history'
    ? { kind: 'mixed-history', playerId } : { kind: 'candidates', playerId, candidateKind: kind }, { limit: kind === 'history' ? 5 : 20 }), crypto.randomUUID()]);
  const query = useInfiniteQuery({ queryKey: key, initialPageParam: null as string | null,
    enabled: !denied && life.current(), retry: false, staleTime: 0, refetchOnWindowFocus: true,
    queryFn: async ({ pageParam, signal }): Promise<Page> => {
      const boundary = life.boundary.current;
      if (!life.current() || signal.aborted) throw abort();
      const controls = { limit: kind === 'history' ? 5 : 20, cursor: pageParam };
      const page = kind === 'history' ? await listMixedDevelopment(playerId, controls, { scope: owner, signal })
        : await listCanonicalFollowupCandidates(playerId, kind, controls, { scope: owner, signal });
      if (!life.current() || !boundary?.current() || signal.aborted) throw abort();
      if (kind === 'history' ? page.schema_version !== 'coaching-mixed-history.v1'
        : page.schema_version !== 'coaching-canonical-candidates.v1' || page.kind !== kind) throw new Error('Invalid coaching page');
      return page;
    }, getNextPageParam: page => page.next_cursor,
  });
  useEffect(() => () => {
    void client.cancelQueries({ queryKey: key, exact: true });
    client.removeQueries({ queryKey: key, exact: true });
  }, [client, key]);
  useEffect(() => {
    if (denied) { void client.cancelQueries({ queryKey: key, exact: true }); client.removeQueries({ queryKey: key, exact: true }); }
  }, [denied, client, key]);
  const pages = query.data?.pages ?? [];
  const coherent = pages.every(page => page.snapshot === pages[0]?.snapshot);
  const available = life.current() && !denied && !query.isError && coherent;
  return { query, pages: available ? pages : [], available: available && pages.length > 0,
    restart: () => { if (life.current() && !denied) void client.resetQueries({ queryKey: key, exact: true }); },
    next: () => { if (available && query.hasNextPage && !query.isFetching) void query.fetchNextPage(); },
    error: query.isError || !coherent };
}

export function useCanonicalFollowups(owner: DraftContext, playerId: string) {
  const life = useLifetime(owner);
  const [denied, setDenied] = useState(false);
  const operation = useManualCommand<Extract<CoachingManualCommand, { action: 'record-followup' }>>(owner);
  const history = useCollection(owner, playerId, 'history', life, denied);
  const actions = useCollection(owner, playerId, 'action', life, denied);
  const focuses = useCollection(owner, playerId, 'focus', life, denied);
  const support = useCollection(owner, playerId, 'support', life, denied);
  const unavailable = denied || operation.denied || !life.current()
    || [history, actions, focuses, support].some(value => accessDenied(value.query.error));
  useEffect(() => { if (unavailable) setDenied(true); }, [unavailable]);
  const candidates = (collection: ReturnType<typeof useCollection>) => unavailable ? [] : collection.pages.flatMap(page =>
    page.schema_version === 'coaching-canonical-candidates.v1' ? page.items : []);
  const anchors = [...candidates(actions), ...candidates(focuses)].filter(item => isAnchor(item, playerId));
  const observations = candidates(support).filter(item => isObservation(item, playerId));
  const items = unavailable ? [] : history.pages.flatMap(page => page.schema_version === 'coaching-mixed-history.v1' ? page.items : []);
  return { unavailable, history, actions, focuses, support, operation, anchors, observations, items,
    choicesReady: !unavailable && [actions, focuses, support].every(value => value.available && !value.query.isFetching),
    refresh: () => { history.restart(); actions.restart(); focuses.restart(); support.restart(); },
  };
}
